/* eslint-disable react/jsx-props-no-spreading */
import App from 'next/app'
import React from 'react'
import pclnExpt from '@pcln/experimentation'
import { ThemeProvider } from 'pcln-design-system'
import { tag } from '@pcln/measure'
import BootstrapDataContext from '../context/BootstrapDataContext'
import ExperimentContext from '../context/ExperimentContext'
import { BootstrapSETI, Referral, Webstats } from '../types'

const PRODUCT_CODES = {
  flights: 1,
  hotels: 5,
  packages: 17,
  home: 16
} as { [key: string]: number }

/**
 *
 * @param {Array<Object>} fullSETI - full list of seti tests being run
 * @param {boolean} isMobile - whether a desktop or mobile browser
 * @param {string} key - product name i.e. packages, hotels, etc
 * @param {Object} referral - traffic info
 * @param {Object} webstats - client info, cguid, user agent..
 */
function initializePclnExpt({
  fullSETI = [],
  isMobile = false,
  key: productCode = '',
  referral,
  webstats
}: {
  fullSETI: Array<BootstrapSETI>
  isMobile: boolean
  key: string
  referral: Referral
  webstats: Webstats
}): void {
  const webAppMetadata = {
    appCode: isMobile ? 'MOBILEWEB' : 'DESKTOPWEB',
    clientIP: webstats.resolvedClientIP,
    globalPartnerCode: 'PCLN',
    partnerCode: 'pcln',
    productID: PRODUCT_CODES[productCode],
    productSubType: 'ALL',
    referralClickID: referral.clickId,
    referralID: referral.id,
    referralSourceID: referral.sourceId,
    serverTimestamp: webstats.requestTime
  }

  if (productCode !== '') {
    pclnExpt.initialize(fullSETI, webAppMetadata, {
      useBatching: true,
      timeOutForApiCalls: 8000 // override the default of 5000
    })
  }
}

export default class MyApp extends App {
  clearBatchHandler = () => {
    const {
      pageProps: {
        bootstrapData: { key = '' }
      }
    } = this.props
    if (key !== '') {
      pclnExpt.clearBatch()
    }
  }

  componentDidMount() {
    const {
      pageProps: { bootstrapData }
    } = this.props
    initializePclnExpt(bootstrapData)
    window.addEventListener('beforeunload', this.clearBatchHandler)
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.clearBatchHandler)
  }

  render() {
    const { Component, pageProps } = this.props
    const { bootstrapSETI = {}, bootstrapData = {} } = pageProps

    // this will add a tag to the boomerang events with the appName and appVersion
    if (typeof window !== 'undefined') {
      tag('appName', bootstrapData.appName || 'next-landing')
      tag('appVersion', bootstrapData.appVersion)
    }

    return (
      <ThemeProvider>
        <BootstrapDataContext.Provider value={bootstrapData}>
          <ExperimentContext.Provider value={bootstrapSETI}>
            <Component {...pageProps} />
          </ExperimentContext.Provider>
        </BootstrapDataContext.Provider>
      </ThemeProvider>
    )
  }
}
